import React, { useEffect, useState } from 'react';
import { whatsappAPI } from './whatsappAPI';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, X, Send } from 'lucide-react';

/**
 * Modal para iniciar uma nova conversa no WhatsApp.
 * Props:
 *  - onClose(): fecha o modal
 *  - onSent(jid): callback quando envia com sucesso (jid do destinatário)
 *  - contatoPreSelecionado: opcional { nome, telefone, origem } — pula a busca
 */
export default function NovaMensagemModal({ onClose, onSent, contatoPreSelecionado }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(contatoPreSelecionado || null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (contatoPreSelecionado) return; // não busca se veio pré-selecionado
    const t = setTimeout(async () => {
      try {
        const r = await whatsappAPI.contatosSistema(q);
        setResults(r.data || []);
      } catch (e) { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, contatoPreSelecionado]);

  const enviar = async () => {
    if (!selected) return toast.error('Selecione um contato');
    if (!text.trim()) return toast.error('Digite a mensagem');
    setSending(true);
    try {
      const r = await whatsappAPI.iniciarConversa({
        telefone: selected.telefone || selected.numero,
        nome: selected.nome,
        texto: text,
      });
      toast.success('Mensagem enviada');
      onSent && onSent(r.data.jid);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao enviar (WhatsApp conectado?)');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Nova Mensagem WhatsApp</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {!contatoPreSelecionado && (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Buscar por nome ou telefone..." value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="input-busca-contato-sistema" />
              </div>
              <div className="border rounded max-h-52 overflow-y-auto">
                {results.length === 0 && <p className="p-3 text-center text-sm text-muted-foreground">Digite para buscar</p>}
                {results.slice(0, 50).map((c, i) => (
                  <button
                    key={i}
                    onClick={() => setSelected(c)}
                    className={`w-full text-left p-2.5 border-b flex justify-between items-center hover:bg-emerald-50 ${selected?.telefone === c.telefone ? 'bg-emerald-100' : ''}`}
                    data-testid={`contato-sistema-${i}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{c.nome}</p>
                      <p className="text-xs font-mono text-muted-foreground">{c.telefone}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded ${c.origem === 'cliente' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{c.origem}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {selected && (
            <div className="bg-emerald-50 p-2 rounded text-sm">
              <strong>Para:</strong> {selected.nome} ({selected.telefone || selected.numero})
            </div>
          )}
          <textarea
            className="w-full border rounded p-2 text-sm min-h-[100px] focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            placeholder="Digite sua mensagem..."
            value={text}
            onChange={e => setText(e.target.value)}
            data-testid="textarea-nova-mensagem"
            autoFocus
          />
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={sending || !selected || !text.trim()} onClick={enviar} data-testid="btn-enviar-nova-msg">
            <Send className="w-4 h-4 mr-2" />{sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
